import { signupIdSchema, signupSlugSchema } from "@ternary/contracts"

/*
 * The signup ids this browser holds, in localStorage. The `tny_signup` cookie is HttpOnly, so the
 * page keeps the id `/start` returned: per slug, so a reload or another tab resumes it instead of
 * starting again (R65), and the slug and guild URL per id, for the join page (G1, G2).
 */

const SIGNUP_KEY = (slug: string) => `tny_signup:${slug}`
const SLUG_KEY = (signupId: string) => `tny_signup_slug:${signupId}`
const GUILD_KEY = (signupId: string) => `tny_signup_guild:${signupId}`
const LAST_SLUG_KEY = "tny_signup_last"

function read(key: string): string | null {
  try {
    return window.localStorage.getItem(key)
  } catch {
    return null
  }
}

function write(key: string, value: string) {
  try {
    window.localStorage.setItem(key, value)
  } catch {
    // Not remembered: the next visit starts a new signup.
  }
}

/** Remembers the signup for the slug, and the slug for the signup. */
export function rememberSignup(slug: string, signupId: string): void {
  write(SIGNUP_KEY(slug), signupId)
  write(SLUG_KEY(signupId), slug)
  write(LAST_SLUG_KEY, slug)
}

/** The slug of the signup this browser held last, for "Start again" after a lost sign-in. */
export function lastSignupSlug(): string | null {
  const stored = read(LAST_SLUG_KEY)
  return signupSlugSchema.safeParse(stored).success ? stored : null
}

/** The signup this browser holds for the slug, or null. */
export function signupIdFor(slug: string): string | null {
  const stored = read(SIGNUP_KEY(slug))
  return signupIdSchema.safeParse(stored).success ? stored : null
}

/**
 * Forgets the slug's signup once the server no longer knows it (its cookie was replaced), only
 * while the stored id is still `signupId`: another tab may have stored a newer one (M7).
 */
export function forgetSignup(slug: string, signupId: string): void {
  try {
    if (window.localStorage.getItem(SIGNUP_KEY(slug)) === signupId) {
      window.localStorage.removeItem(SIGNUP_KEY(slug))
    }
  } catch {
    // Blocked storage held nothing.
  }
}

/** The slug of a signup this browser started, or null. */
export function slugFor(signupId: string): string | null {
  return read(SLUG_KEY(signupId))
}

export function rememberGuildUrl(signupId: string, guildUrl: string): void {
  write(GUILD_KEY(signupId), guildUrl)
}

export function guildUrlFor(signupId: string): string | null {
  return read(GUILD_KEY(signupId))
}
